import type { KeyboardEvent as ReactKeyboardEvent, ReactNode } from 'react'
import { createContext, useCallback, useContext, useState } from 'react'
import { Loader } from '@systemeio/components'
import { useMutationState, type Mutation } from '@tanstack/react-query'
import { useTranslation } from 'next-i18next'
import { twMerge } from 'tailwind-merge'
import { usePageComments } from 'modules/comments/api/get-page-comments'
import { usePinnedComments } from 'modules/comments/api/get-pinned-comments'
import { commentsKeys } from 'modules/comments/api/key-factory'
import type { AddCommentRequestType } from 'modules/comments/api/types'
import { Comment } from 'modules/comments/components/comment'
import { CommentSortSelector } from 'modules/comments/components/comment-sort-selector'
import type { CommentInterface } from 'modules/comments/components/comment/comment.types'
import { SubmittedComment } from 'modules/comments/components/comment/submitted-comment'
import { CommentListWithLoader } from 'modules/comments/components/comments-list/comment-list-with-loader'
import type { CommentsContextType } from 'modules/comments/components/comments-list/comments-list.types'
import { MessageBox } from 'modules/comments/components/message-box/message-box'
import { StickyMessageBox } from 'modules/comments/components/message-box/sticky-message-box'
import { StickyMessageBoxWithReply } from 'modules/comments/components/message-box/sticky-message-box-with-reply'
import {
  CommentSortOptions,
  type CommentSortEnum,
  type CommentsTypeEnum,
  type OnErrorType,
} from 'modules/comments/types'
import {
  getCommentSortPreference,
  saveCommentSortPreference,
} from 'modules/comments/utils/comment-preferences'
import { getDefaultSortOption } from 'modules/comments/utils/comment-sorting'
import type { CommentsHighlightContext } from 'modules/community/components/post/types'
import { CommentsListSkeleton } from './comments-list-skeleton'
import { PinnedCommentsList } from './pinned-comments-list'

export const CommentsContext = createContext<CommentsContextType | undefined>(undefined)

export function useCommentsContext(): CommentsContextType {
  const context = useContext(CommentsContext)

  if (!context) {
    throw new Error('useCommentsContext must be used within a CommentsContextProvider')
  }

  return context
}

interface BaseCommentListProps {
  commentsType: CommentsTypeEnum
  shouldFetch?: boolean
  readOnly?: boolean
  onError?: OnErrorType
  targetCommentId?: number
  commentsHighlightContext?: CommentsHighlightContext
}
type CommentsCourseAndCommunityProps =
  | {
      commentsType: CommentsTypeEnum.Community
      community: {
        communityPath: string
        postId: number
      }
    }
  | {
      commentsType: CommentsTypeEnum.LightLecture
      lightLecture: {
        lectureId: number
      }
    }
  | {
      commentsType: CommentsTypeEnum.Page
      course: {
        pageId: number
      }
    }
export type CommentsProps = BaseCommentListProps & CommentsCourseAndCommunityProps

const CommentsListInner = (props: CommentsProps): ReactNode => {
  const {
    commentsType,
    shouldFetch = true,
    readOnly = false,
    onError,
    targetCommentId,
    commentsHighlightContext,
  }: CommentsProps = props

  const { t } = useTranslation()

  const isCommunityProps = 'community' in props
  const isLightLectureProps = 'lightLecture' in props
  const resourceId = isCommunityProps
    ? props.community.postId
    : isLightLectureProps
      ? props.lightLecture.lectureId
      : props.course.pageId

  const highlightTargetCommentId =
    targetCommentId ?? commentsHighlightContext?.targetCommentId ?? null
  const [highlightedCommentId, setHighlightedCommentId] = useState<number | null>(
    highlightTargetCommentId,
  )
  const finishHighlight = useCallback((commentId: number): void => {
    setHighlightedCommentId(current => (current === commentId ? null : current))
  }, [])

  const [activeComment, setActiveComment] = useState<CommentInterface | null>(null)
  const [sortOption, setSortOption] = useState(
    () => getCommentSortPreference() || getDefaultSortOption(isCommunityProps),
  )

  const getPageCommentsQuery = usePageComments({
    commentsType,
    pageId: resourceId,
    parentId: null,
    sortOption,
    targetCommentId,
    enabled: shouldFetch,
    onError,
  })
  const pinnedQuery = usePinnedComments({
    postId: resourceId,
    targetCommentId,
    enabled: isCommunityProps && shouldFetch,
  })
  const comments = getPageCommentsQuery.data ?? []
  const pinnedComments = pinnedQuery.data ?? []
  const rootAdditions = useMutationState<
    {
      mutationId: number
      variables: AddCommentRequestType<CommentsTypeEnum>
      submittedAt: number
      commentId: number | undefined
    }[],
    Mutation<CommentInterface, Error, AddCommentRequestType<CommentsTypeEnum>>
  >({
    filters: {
      exact: true,
      mutationKey: commentsKeys.add(commentsType, resourceId),
    },
    select: mutation => {
      const { variables, status, submittedAt, data: addedComment } = mutation.state
      if (variables?.parent !== null || (status !== 'pending' && status !== 'success')) return []
      return [
        { mutationId: mutation.mutationId, variables, submittedAt, commentId: addedComment?.id },
      ]
    },
  }).flat()
  const pendingRootComments = rootAdditions.filter(
    ({ commentId }) =>
      commentId === undefined ||
      (!getPageCommentsQuery.deletedCommentIds.includes(commentId) &&
        !comments.some(comment => comment.id === commentId) &&
        !pinnedComments.some(comment => comment.id === commentId) &&
        !getPageCommentsQuery.resolvedFocusCommentIds.includes(commentId)),
  )
  const isReloading = getPageCommentsQuery.isLoading

  const handleSortChange = (nextSort: CommentSortEnum): void => {
    if (nextSort === sortOption) return
    saveCommentSortPreference(nextSort)
    setSortOption(nextSort)
  }

  const isReplyMessageBox = activeComment !== null
  const isAllowCommentingAndNotReplyMessageBox = !isReplyMessageBox && !readOnly

  const showInitialSkeletonLoading =
    getPageCommentsQuery.isFetching && comments.length === 0 && pendingRootComments.length === 0

  const handleKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>): void => {
    if (activeComment === null || event.key !== 'Escape') return
    event.stopPropagation()
    setActiveComment(null)
  }

  const replyInfo =
    !!activeComment && isCommunityProps
      ? {
          userId: activeComment.userId,
          displayName: activeComment.userName,
          profileImage: activeComment.userAvatarUrl,
        }
      : undefined

  const extraProviderValue = isCommunityProps
    ? {
        community: {
          communityPath: props.community.communityPath,
          postId: props.community.postId,
        },
      }
    : isLightLectureProps
      ? {
          lightLecture: {
            lectureId: props.lightLecture.lectureId,
          },
        }
      : {
          course: {
            pageId: props.course.pageId,
          },
        }

  const pendingRootElements = (
    sortOption === CommentSortOptions.NEWEST_FIRST
      ? pendingRootComments.toReversed()
      : pendingRootComments
  ).map(({ mutationId, variables, submittedAt }) => (
    <SubmittedComment key={mutationId} variables={variables} submittedAt={submittedAt} level={0} />
  ))

  return (
    <CommentsContext.Provider
      value={{
        commentsType,
        resourceId,
        pinnedComments,
        readOnly,
        extra: extraProviderValue,
        currentSortOption: sortOption,
        targetCommentId,
      }}
    >
      <div data-comments-list="" className="relative" onKeyDown={handleKeyDown}>
        <div className="flex flex-col items-stretch space-y-5 [overflow-anchor:none] md:space-y-6">
          {(comments.length > 0 || pinnedComments.length > 0) && (
            <CommentSortSelector
              currentSort={sortOption}
              onSortChange={handleSortChange}
              disabled={getPageCommentsQuery.isLoading}
            />
          )}

          {!isCommunityProps && isAllowCommentingAndNotReplyMessageBox && (
            <MessageBox
              parentId={null}
              onError={onError}
              onCommentAdded={setHighlightedCommentId}
            />
          )}

          {isCommunityProps && (
            <PinnedCommentsList
              setActiveComment={setActiveComment}
              activeComment={activeComment}
              onError={onError}
              highlightedCommentId={highlightedCommentId}
              onHighlightComplete={finishHighlight}
            />
          )}

          {showInitialSkeletonLoading ? (
            <CommentsListSkeleton />
          ) : (
            <div className="relative">
              {isReloading && (
                <div
                  className="absolute inset-0 z-10 flex items-start justify-center pt-20"
                  role="status"
                  aria-live="polite"
                  aria-label="Reloading comments"
                >
                  <Loader size="xl" />
                </div>
              )}

              <div
                className={twMerge(
                  'space-y-5 transition-all duration-300 md:space-y-6',
                  isReloading && 'shimmer-loading pointer-events-none opacity-50 select-none',
                )}
                inert={isReloading ? true : undefined}
              >
                {sortOption === CommentSortOptions.NEWEST_FIRST && pendingRootElements}
                <CommentListWithLoader
                  items={comments}
                  renderItem={comment => {
                    const highlighted = commentsHighlightContext?.searchHighlightedComments?.find(
                      h => h.id === comment.id,
                    )
                    return (
                      <Comment
                        key={comment.id}
                        data={{ ...highlighted, ...comment }}
                        level={0}
                        setActiveComment={setActiveComment}
                        activeComment={activeComment}
                        onError={onError}
                        highlightedCommentId={highlightedCommentId}
                        onHighlightComplete={finishHighlight}
                      />
                    )
                  }}
                  onLoadMore={() => {
                    if (getPageCommentsQuery.isError) void getPageCommentsQuery.refetch()
                    else void getPageCommentsQuery.fetchNextPage()
                  }}
                  isLoadingMore={getPageCommentsQuery.isFetching}
                  loadMoreLabel={t('comments.components.comments.load_more_label')}
                />
                {sortOption !== CommentSortOptions.NEWEST_FIRST && pendingRootElements}
              </div>
            </div>
          )}
        </div>

        {/* NOTE: It's a workaround to have the comment field re-created due to React life-cycle. Since Slate is uncontrollable and we can only use initValue on mount component */}
        {!isCommunityProps || readOnly ? null : isAllowCommentingAndNotReplyMessageBox ? (
          <StickyMessageBox
            parentId={null}
            onError={onError}
            onCommentAdded={setHighlightedCommentId}
          />
        ) : (
          <StickyMessageBoxWithReply
            parentId={
              activeComment &&
              (Array.isArray(activeComment.parent) ? activeComment.id : activeComment.parent.id)
            }
            setActiveComment={setActiveComment}
            onError={onError}
            replyInfo={replyInfo}
            onCommentAdded={setHighlightedCommentId}
          />
        )}
      </div>
    </CommentsContext.Provider>
  )
}

export const CommentsList = (props: CommentsProps): ReactNode => {
  const resourceId =
    'community' in props
      ? props.community.postId
      : 'lightLecture' in props
        ? props.lightLecture.lectureId
        : props.course.pageId
  return (
    <CommentsListInner
      key={`${props.commentsType}:${resourceId}:${
        props.targetCommentId ?? props.commentsHighlightContext?.targetCommentId ?? ''
      }`}
      {...props}
    />
  )
}
